import { Box, Typography, Tooltip } from '@mui/material';

export interface TrendChartDataPoint {
  date: string;
  value: number | null;
  label?: string;
}

export interface TrendChartColorThreshold {
  value: number;
  color: string;
}

export interface TrendChartProps {
  data: TrendChartDataPoint[];
  height?: number;
  barWidth?: number;
  barGap?: number;
  emptyText?: string;
  colorThresholds?: TrendChartColorThreshold[];
  valueFormatter?: (value: number) => string;
  useNormalization?: boolean;
  normalizationRange?: [number, number];
}

/**
 * TrendChart - Reusable mini bar chart component for displaying trends
 *
 * @example
 * // ROAS trend
 * <TrendChart
 *   data={dailyData.map(d => ({ date: d.date, value: d.roas }))}
 *   colorThresholds={[
 *     { value: 1.25, color: '#10b981' },
 *     { value: 1.05, color: '#22c55e' },
 *     { value: 0.8, color: '#f59e0b' },
 *     { value: 0, color: '#ef4444' },
 *   ]}
 *   valueFormatter={(v) => `${(v * 100).toFixed(1)}%`}
 * />
 *
 * @example
 * // RPC trend
 * <TrendChart
 *   data={dailyData.map(d => ({ date: d.date, value: d.avgRpc }))}
 *   useNormalization
 *   colorThresholds={[
 *     { value: 0.6, color: '#10b981' },
 *     { value: 0.4, color: '#22c55e' },
 *     { value: 0.25, color: '#f59e0b' },
 *     { value: 0, color: '#ef4444' },
 *   ]}
 *   valueFormatter={(v) => `$${v.toFixed(2)}`}
 * />
 */
const TrendChart = ({
  data,
  height = 32,
  barWidth = 6,
  barGap = 2,
  emptyText = '-',
  colorThresholds,
  valueFormatter,
  useNormalization = false,
  normalizationRange = [0, 200],
}: TrendChartProps) => {
  // Return empty state if no data
  if (!data || data.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  // Sort by date
  const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));

  // Calculate min/max for normalization
  let minValue = 0;
  let maxValue = 0;
  if (useNormalization) {
    const values = sorted.map(d => d.value || 0).filter(v => v > 0);
    if (values.length > 0) {
      minValue = Math.min(...values);
      maxValue = Math.max(...values) + (Math.max(...values) * 0.1); // Add 10% padding
    }
  }

  // Get color based on value and thresholds
  const getColor = (value: number | null): string => {
    if (value === null || value === undefined) return '#9ca3af';
    if (!colorThresholds || colorThresholds.length === 0) return '#4CAEB8';

    // Sort thresholds descending and find first match
    const sortedThresholds = [...colorThresholds].sort((a, b) => b.value - a.value);
    for (const threshold of sortedThresholds) {
      if (value >= threshold.value) {
        return threshold.color;
      }
    }
    return sortedThresholds[sortedThresholds.length - 1].color;
  };

  // Calculate bar height based on value
  const calculateHeight = (value: number | null): number => {
    if (value === null || value === undefined || value === 0) return 2;

    let normalizedValue: number;

    if (useNormalization) {
      // Normalize to range
      if (maxValue > minValue && value > 0) {
        normalizedValue = ((value - minValue) / (maxValue - minValue)) * normalizationRange[1];
      } else {
        normalizedValue = 0;
      }
    } else {
      normalizedValue = value * 100; // For percentages like ROAS
    }

    // Scale to bar height
    if (normalizedValue === 0) {
      return 2;
    } else if (normalizedValue < 50) {
      return Math.max(3, Math.min(8, normalizedValue * 0.16));
    } else if (normalizedValue < 100) {
      return Math.max(8, Math.min(16, 8 + (normalizedValue - 50) * 0.16));
    } else if (normalizedValue < 200) {
      return Math.max(16, Math.min(28, 16 + (normalizedValue - 100) * 0.12));
    } else {
      return Math.max(28, Math.min(height, 28 + Math.min(4, (normalizedValue - 200) * 0.02)));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: `${barGap}px`,
        height,
      }}
    >
      {sorted.map((point) => {
        const barHeight = calculateHeight(point.value);
        const color = getColor(point.value);

        // Format date for tooltip
        const dateObj = new Date(point.date);
        const dayLabel = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        // Format value for tooltip
        const displayValue = point.value !== null && point.value !== undefined
          ? valueFormatter
            ? valueFormatter(point.value)
            : point.value.toFixed(2)
          : 'N/A';

        const tooltipTitle = point.label || `${dayLabel}: ${displayValue}`;

        return (
          <Tooltip key={point.date} title={tooltipTitle}>
            <Box
              sx={{
                width: barWidth,
                height: barHeight,
                backgroundColor: color,
                borderRadius: '1px',
                cursor: 'default',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default TrendChart;
